import React, { useCallback, useEffect, useState } from 'react';
import useGroupLabels from 'hooks/useGroupLabels';
import { formatGroupCell } from 'utils/groupLabel';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { ChevronRight } from 'lucide-react';

import styles from './TrainingResultsView.module.css';
import routes from 'routes';

import {
  getTrainingResultsMeetSummary,
  getTrainingResults,
  exportTrainingResultsToFile,
} from 'api/resultApi';
import { convertMillisecondsToRecord } from 'utils/time';
import { convertDateTime } from 'utils/time';
import { exportAndDownloadFile } from 'utils/fileDownload';
import { getUserPermissionLevel } from 'services/auth/tokenService';
import { UserPermissionLevel } from 'constants/auth';

import AdminTablePageLayout from 'components/admin/AdminTablePageLayout';
import MeetPageHeader from 'components/NewDesign/MeetPageHeader';
import CustomNewButton from 'components/NewDesign/CustomNewButton';

const BADGE_CLASSES = {
  'Sprint Training': styles.badgeSprint,
  'Flying Start': styles.badgeFlying,
  'Agility Training': styles.badgeAgility,
  'Shuttle Run': styles.badgeShuttle,
  'Lap Training': styles.badgeLap,
  '40 Yard Dash': styles.badgeYards,
};

const isYardsEvent = (eventType) => eventType === '40 Yard Dash';

const metersToYards = (m) => Math.round(m / 0.9144);

// 1 m/s = 2.2369362920544 mph
const msToMph = (v) => v * 2.2369362920544;

const getEventCardSplitsLabel = (summary) => {
  if (isYardsEvent(summary.eventType)) {
    const yardDistances = [...summary.splitDistances, summary.finishDistance]
      .map((d) => `${metersToYards(d)} yd`)
      .join(' \u2192 ');
    return `Splits: ${yardDistances}`;
  }

  const distances = [...summary.splitDistances, summary.finishDistance]
    .map((d) => `${d}m`)
    .join(' \u2192 ');

  switch (summary.eventType) {
    case 'Sprint Training':
    case 'Agility Training':
      return `Splits: ${distances}`;
    case 'Flying Start':
      return `Start: ${summary.startDistance}m \u2192 ${distances}`;
    case 'Shuttle Run':
      return `Distance: ${summary.finishDistance}m`;
    case 'Lap Training': {
      const lapLabel = `Lap Distance: ${summary.finishDistance}m`;
      if (summary.splitDistances.length === 0) {
        return lapLabel;
      }
      const splitsOnly = summary.splitDistances
        .map((d) => `${d}m`)
        .join(' \u2192 ');
      return `${lapLabel}. Splits: ${splitsOnly}`;
    }
    default:
      return `Splits: ${distances}`;
  }
};

const TrainingResultsView = ({ clubId, meetId }) => {
  // The athlete's group beside their name reads "11 M", or "Y6 M" for a club that groups by year level, and the
  // sort choice is named to match.
  const { groupingBasis, words } = useGroupLabels(clubId);
  const navigate = useNavigate();
  const userPermissionLevel = getUserPermissionLevel();

  const [loading, setLoading] = useState(true);
  const [meetSummary, setMeetSummary] = useState(null);
  const [activeCardIndex, setActiveCardIndex] = useState(null);
  const [trainingResults, setTrainingResults] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [expandedAthletes, setExpandedAthletes] = useState(new Set());
  const [allExpanded, setAllExpanded] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [showVelocity, setShowVelocity] = useState(false);

  const fetchMeetSummary = useCallback(async () => {
    try {
      const data = await getTrainingResultsMeetSummary(clubId, meetId);
      setMeetSummary(data);
      if (data.eventSummaries?.length > 0) {
        setActiveCardIndex(0);
      }
    } catch (err) {
      console.error('[TrainingResults] Fetch summary error:', err);
      toast.error('Failed to load training results summary');
    } finally {
      setLoading(false);
    }
  }, [clubId, meetId]);

  useEffect(() => {
    fetchMeetSummary();
  }, [fetchMeetSummary]);

  const fetchResults = useCallback(
    async (eventIds) => {
      setLoadingResults(true);
      try {
        const data = await getTrainingResults(clubId, meetId, eventIds);
        setTrainingResults(data);
        setExpandedAthletes(new Set());
        setAllExpanded(false);
      } catch (err) {
        console.error('[TrainingResults] Fetch results error:', err);
        toast.error('Failed to load training results');
      } finally {
        setLoadingResults(false);
      }
    },
    [clubId, meetId]
  );

  useEffect(() => {
    if (meetSummary && activeCardIndex !== null) {
      const card = meetSummary.eventSummaries[activeCardIndex];
      if (card?.eventIds?.length > 0) {
        fetchResults(card.eventIds);
      }
    }
  }, [meetSummary, activeCardIndex, fetchResults]);

  const handleCardSelect = (index) => {
    setActiveCardIndex(index);
  };

  const toggleAthlete = (athleteId) => {
    setExpandedAthletes((prev) => {
      const next = new Set(prev);
      if (next.has(athleteId)) {
        next.delete(athleteId);
      } else {
        next.add(athleteId);
      }
      return next;
    });
  };

  const toggleExpandAll = () => {
    if (allExpanded) {
      setExpandedAthletes(new Set());
      setAllExpanded(false);
    } else {
      if (trainingResults?.athletes) {
        setExpandedAthletes(
          new Set(trainingResults.athletes.map((a) => a.athleteId))
        );
      }
      setAllExpanded(true);
    }
  };

  const formatTime = (ms) => {
    if (ms == null) return '\u2014';
    return convertMillisecondsToRecord(ms, 2);
  };

  const formatReaction = (ms) => {
    if (ms == null) return '\u2014';
    return (ms / 1000).toFixed(3);
  };

  // Safe round-half-up for velocity to avoid floating-point quirks of toFixed.
  // e.g. (6.17 * 10) = 61.7 → round → 62 → /10 → 6.2
  const formatVelocity = (v) => {
    if (v == null) return null;
    const value = isYardsEvent(trainingResults?.eventType) ? msToMph(v) : v;
    return (Math.round(value * 10 + Number.EPSILON) / 10).toFixed(1);
  };

  const getSortedAthletes = () => {
    if (!trainingResults?.athletes) return [];
    const athletes = [...trainingResults.athletes];

    // Gender priority: F before M (lower number sorts first)
    const genderRank = (g) => (g === 'F' ? 0 : g === 'M' ? 1 : 2);

    const compareLastName = (a, b) => {
      const cmp = a.lastName.localeCompare(b.lastName);
      if (cmp !== 0) return cmp;
      return a.firstName.localeCompare(b.firstName);
    };

    if (sortBy === 'name') {
      athletes.sort(compareLastName);
    } else if (sortBy === 'best') {
      athletes.sort((a, b) => {
        const aFinish = a.bestFinishMs ?? Infinity;
        const bFinish = b.bestFinishMs ?? Infinity;
        return aFinish - bFinish;
      });
    } else if (sortBy === 'runs') {
      athletes.sort((a, b) => b.runCount - a.runCount);
    } else if (sortBy === 'age') {
      athletes.sort((a, b) => {
        const ageCmp = (a.athleteAge ?? Infinity) - (b.athleteAge ?? Infinity);
        if (ageCmp !== 0) return ageCmp;
        const genderCmp = genderRank(a.gender) - genderRank(b.gender);
        if (genderCmp !== 0) return genderCmp;
        return compareLastName(a, b);
      });
    } else if (sortBy === 'gender') {
      athletes.sort((a, b) => {
        const genderCmp = genderRank(a.gender) - genderRank(b.gender);
        if (genderCmp !== 0) return genderCmp;
        const ageCmp = (a.athleteAge ?? Infinity) - (b.athleteAge ?? Infinity);
        if (ageCmp !== 0) return ageCmp;
        return compareLastName(a, b);
      });
    }

    return athletes;
  };

  const availableEventTypes = meetSummary?.eventSummaries
    ? Array.from(
        new Set(meetSummary.eventSummaries.map((s) => s.eventType))
      ).sort()
    : [];

  const filteredEventSummaries =
    meetSummary?.eventSummaries?.filter(
      (s) => eventTypeFilter === 'all' || s.eventType === eventTypeFilter
    ) || [];

  const activeCard =
    meetSummary && activeCardIndex !== null
      ? meetSummary.eventSummaries[activeCardIndex]
      : null;

  const handleExportToFile = async () => {
    await exportAndDownloadFile(
      exportTrainingResultsToFile,
      [{ clubId, meetId }],
      '',
      'Training results were successfully exported',
      'Fetching training results data from the server...'
    );
  };

  const handleEventTypeFilterChange = (e) => {
    const newFilter = e.target.value;
    setEventTypeFilter(newFilter);
    const newFiltered =
      meetSummary?.eventSummaries?.filter(
        (s) => newFilter === 'all' || s.eventType === newFilter
      ) || [];
    if (newFiltered.length > 0) {
      const firstIndex = meetSummary.eventSummaries.indexOf(newFiltered[0]);
      setActiveCardIndex(firstIndex);
    } else {
      setActiveCardIndex(null);
    }
  };

  return (
    <AdminTablePageLayout loading={loading}>
      <MeetPageHeader
        title="Training Results"
        meetName={meetSummary?.meetName}
        meetDate={
          meetSummary ? convertDateTime(meetSummary.meetDate).date : undefined
        }
        onBackToMeet={() =>
          navigate(routes.admin.showMeet.url(clubId, meetId))
        }
      >
        {availableEventTypes.length > 1 && (
          <select
            className={styles.filterSelect}
            value={eventTypeFilter}
            onChange={handleEventTypeFilterChange}
          >
            <option value="all">All Event Types</option>
            {availableEventTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        )}
        {userPermissionLevel >= UserPermissionLevel.Official && (
          <CustomNewButton variant="info" onClick={handleExportToFile}>
            Export to file
          </CustomNewButton>
        )}
      </MeetPageHeader>

      {/* Event Cards */}
      {filteredEventSummaries.length > 0 && (
        <div className={styles.eventCards}>
          {filteredEventSummaries.map((summary) => {
            const index = meetSummary.eventSummaries.indexOf(summary);
            return (
            <div
              key={index}
              className={`${styles.eventCard} ${activeCardIndex === index ? styles.eventCardActive : ''}`}
              onClick={() => handleCardSelect(index)}
            >
              <span
                className={`${styles.eventTypeBadge} ${BADGE_CLASSES[summary.eventType] || styles.badgeSprint}`}
              >
                {summary.eventType}
              </span>
              <div className={styles.eventName}>
                {isYardsEvent(summary.eventType)
                  ? summary.eventType
                  : `${summary.eventType} ${summary.finishDistance}m`}
              </div>
              <div className={styles.eventSplits}>
                {getEventCardSplitsLabel(summary)}
              </div>
              <div className={styles.eventStats}>
                <div className={styles.stat}>
                  <span className={styles.statValue}>
                    {summary.athleteCount}
                  </span>
                  <span className={styles.statLabel}>
                    {summary.athleteCount === 1 ? 'Athlete' : 'Athletes'}
                  </span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statValue}>
                    {summary.totalRuns}
                  </span>
                  <span className={styles.statLabel}>
                    {summary.eventType === 'Lap Training'
                      ? 'Total Laps'
                      : 'Total Runs'}
                  </span>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Filters Bar */}
      {activeCard && (
        <div className={styles.filtersBar}>
          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Sort by</span>
            <select
              className={styles.filterSelect}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="name">Name</option>
              <option value="age">{words.value}</option>
              <option value="gender">Gender</option>
              <option value="best">Best Result</option>
              <option value="runs">Most Runs</option>
            </select>
          </div>
          <label className={styles.velocityToggle}>
            <input
              type="checkbox"
              checked={showVelocity}
              onChange={(e) => setShowVelocity(e.target.checked)}
            />
            {isYardsEvent(trainingResults?.eventType)
              ? 'Include Velocity (mph)'
              : 'Include Velocity (m/s)'}
          </label>
          <button className={styles.expandToggle} onClick={toggleExpandAll}>
            {allExpanded ? 'Collapse All' : 'Expand All'}
          </button>
        </div>
      )}

      {/* Results Table */}
      {loadingResults && (
        <div className={styles.loadingMessage}>Loading results...</div>
      )}

      {!loadingResults && trainingResults && (
        <div className={styles.resultsPanel}>
          <table className={styles.resultsTable}>
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th style={{ minWidth: 230, whiteSpace: 'nowrap' }}>Athlete</th>
                <th className={styles.thNumeric} style={{ width: 60 }}>ID</th>
                <th className={styles.thNumeric} style={{ width: '100%' }}>
                  {trainingResults.isLapTraining ? 'Laps' : 'Runs'}
                </th>
                {trainingResults.hasReaction && (
                  <th className={styles.thNumeric}>Best Reaction</th>
                )}
                {trainingResults.splitDistances.map((dist) => (
                  <th key={dist} className={styles.thNumeric}>
                    {isYardsEvent(trainingResults.eventType)
                      ? `Best ${metersToYards(dist)}yd`
                      : `Best ${dist}m`}
                  </th>
                ))}
                <th className={styles.thNumeric}>
                  {trainingResults.isLapTraining
                    ? 'Best Lap'
                    : isYardsEvent(trainingResults.eventType)
                    ? `Best ${metersToYards(trainingResults.finishDistance)}yd`
                    : `Best ${trainingResults.finishDistance}m`}
                </th>
              </tr>
            </thead>
            <tbody>
              {getSortedAthletes().map((athlete) => {
                const isExpanded = expandedAthletes.has(athlete.athleteId);
                return (
                  <React.Fragment key={athlete.athleteId}>
                    {/* Summary Row */}
                    <tr
                      className={`${styles.athleteRow} ${isExpanded ? styles.athleteRowExpanded : ''}`}
                      onClick={() => toggleAthlete(athlete.athleteId)}
                    >
                      <td>
                        <span
                          className={`${styles.expandIcon} ${isExpanded ? styles.expandIconOpen : ''}`}
                        >
                          <ChevronRight size={14} />
                        </span>
                      </td>
                      <td>
                        <span className={styles.athleteName}>
                          {athlete.firstName} {athlete.lastName}
                        </span>
                        <span className={styles.athleteAge}>
                          {formatGroupCell(groupingBasis, athlete.athleteAge)} {athlete.gender}
                        </span>
                      </td>
                      <td className={styles.numeric}>
                        <span className={styles.athleteId}>
                          {athlete.athleteNumber || '\u2014'}
                        </span>
                      </td>
                      <td className={styles.numeric}>
                        <span className={styles.runCount}>
                          {athlete.runCount}
                        </span>
                      </td>
                      {trainingResults.hasReaction && (
                        <td className={styles.numeric}>
                          {formatReaction(athlete.bestReactionMs)}
                        </td>
                      )}
                      {trainingResults.splitDistances.map((dist) => {
                        const split = athlete.bestSplits?.find(
                          (s) => s.distance === dist
                        );
                        return (
                          <td key={dist} className={styles.numeric}>
                            {formatTime(split?.timeInMilliseconds)}
                          </td>
                        );
                      })}
                      <td className={`${styles.numeric} ${styles.best}`}>
                        {formatTime(athlete.bestFinishMs)}
                      </td>
                    </tr>

                    {/* Detail Row */}
                    {isExpanded && (
                      <tr className={styles.detailContainer}>
                        <td
                          colSpan={
                            4 +
                            trainingResults.splitDistances.length +
                            1 +
                            (trainingResults.hasReaction ? 1 : 0)
                          }
                        >
                          <div className={styles.detailInner}>
                            <table className={styles.detailTable}>
                              <tbody>
                                <tr className={styles.detailHeaderRow}>
                                  <td
                                    style={{
                                      minWidth: trainingResults.isLapTraining
                                        ? 120
                                        : 60,
                                      padding: '10px 12px',
                                      fontSize: 11,
                                      fontWeight: 600,
                                      color: '#8b95a5',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.4px',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {trainingResults.isLapTraining
                                      ? `${athlete.runs.length} ${athlete.runs.length === 1 ? 'Lap' : 'Laps'} \u00B7 ${athlete.runs.length * trainingResults.finishDistance}m \u00B7 ${formatTime(athlete.runs.reduce((sum, r) => sum + (r.finishMs || 0), 0))}`
                                      : 'Run'}
                                  </td>
                                  {trainingResults.hasReaction && (
                                    <td
                                      style={{
                                        padding: '10px 12px',
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: '#8b95a5',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.4px',
                                        textAlign: 'right',
                                      }}
                                    >
                                      Reaction
                                    </td>
                                  )}
                                  {trainingResults.hasStart && (
                                    <td
                                      style={{
                                        padding: '10px 12px',
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: '#8b95a5',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.4px',
                                        textAlign: 'right',
                                      }}
                                    >
                                      Start {trainingResults.startDistance}m
                                    </td>
                                  )}
                                  {trainingResults.splitDistances.map(
                                    (dist) => (
                                      <td
                                        key={dist}
                                        style={{
                                          padding: '10px 12px',
                                          fontSize: 11,
                                          fontWeight: 600,
                                          color: '#8b95a5',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.4px',
                                          textAlign: 'right',
                                        }}
                                      >
                                        {isYardsEvent(trainingResults.eventType)
                                          ? `${metersToYards(dist)}yd`
                                          : `${dist}m`}
                                      </td>
                                    )
                                  )}
                                  <td
                                    style={{
                                      padding: '10px 12px',
                                      fontSize: 11,
                                      fontWeight: 600,
                                      color: '#8b95a5',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.4px',
                                      textAlign: 'right',
                                    }}
                                  >
                                    {isYardsEvent(trainingResults.eventType)
                                      ? `${metersToYards(trainingResults.finishDistance)}yd`
                                      : `${trainingResults.finishDistance}m`}
                                  </td>
                                </tr>
                                {athlete.runs.map((run) => (
                                  <tr key={run.runNumber}>
                                    <td>
                                      <span className={styles.runLabel}>
                                        {trainingResults.isLapTraining
                                          ? `Lap ${run.runNumber}`
                                          : `Run ${run.runNumber}`}
                                      </span>
                                    </td>
                                    {trainingResults.hasReaction && (
                                      <td
                                        className={`${styles.detailNumeric} ${run.isReactionBest ? styles.isBest : ''}`}
                                      >
                                        {formatReaction(run.reactionMs)}
                                      </td>
                                    )}
                                    {trainingResults.hasStart && (
                                      <td className={styles.detailNumeric}>
                                        0.00
                                      </td>
                                    )}
                                    {trainingResults.splitDistances.map(
                                      (dist) => {
                                        const split = run.splits?.find(
                                          (s) => s.distance === dist
                                        );
                                        return (
                                          <td
                                            key={dist}
                                            className={`${styles.detailNumeric} ${split?.isBest ? styles.isBest : ''}`}
                                          >
                                            {showVelocity &&
                                              split?.velocity != null && (
                                                <span
                                                  className={
                                                    styles.velocityValue
                                                  }
                                                >
                                                  ({formatVelocity(
                                                    split.velocity
                                                  )})
                                                </span>
                                              )}
                                            {formatTime(
                                              split?.timeInMilliseconds
                                            )}
                                          </td>
                                        );
                                      }
                                    )}
                                    <td
                                      className={`${styles.detailNumeric} ${run.isFinishBest ? styles.isBest : ''}`}
                                    >
                                      {showVelocity &&
                                        run.finishVelocity != null && (
                                          <span
                                            className={styles.velocityValue}
                                          >
                                            ({formatVelocity(run.finishVelocity)})
                                          </span>
                                        )}
                                      {formatTime(run.finishMs)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && meetSummary?.eventSummaries?.length === 0 && (
        <div className={styles.emptyState}>
          No training results found for this meet.
        </div>
      )}
    </AdminTablePageLayout>
  );
};

TrainingResultsView.propTypes = {
  clubId: PropTypes.string.isRequired,
  meetId: PropTypes.string.isRequired,
};

export default TrainingResultsView;
